import React from 'react';
import './SkillOverlay.css';
import { EEntityType, entityTypeToLink } from '@sharedInterfaces/globalEnums';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import getSkill from '@src/APIs/graphQl/Skill/getSkill';
import SkillPage from '@src/App/Pages/EntityPages/Skills/SkillPage/SkillPage';
import type { AppState } from '@store/store';
import { useSelector } from 'react-redux';

import Overlay from '../Overlay/Overlay';

interface SkillOverlayProps {
    skillId: number;
    children: React.ReactNode;
    disabled?: boolean;
    onClick?: string | (() => void);
    open?: boolean;
    onClose?: () => void;
}

/**
 * SkillOverlay
 *
 * @param {SkillOverlayProps} props - The props for the SkillOverlay component
 * @returns {JSX.Element} - The rendered SkillOverlay component
 */
export default function SkillOverlay(props: SkillOverlayProps) {
    const entity = useSelector((state: AppState) => state.skills.find((a) => a.id === props.skillId));
    const permissions = useSelector((state: AppState) => state.permissions);
    const isAllowed =
        checkPermissions('Skills', 'Retrieve', permissions) || checkPermissions('Skills', 'RetrieveOwn', permissions);
    const disabled = !isAllowed || props.disabled;
    const [error, setError] = React.useState<Error | null>(null);

    // Use onClick if provided, otherwise fallback to default link
    const systemBarNavigateUrl =
        typeof props.onClick === 'string' ? props.onClick : entityTypeToLink(props.skillId, EEntityType.SKILL);

    return (
        <Overlay
            disabled={disabled}
            triggerItem={props.children}
            loaded={entity !== null}
            error={error}
            systemBarNavigateUrl={systemBarNavigateUrl}
            open={props.open}
            onClose={props.onClose}
            onFirstOpen={() => {
                getSkill(props.skillId).catch(setError);
            }}
        >
            {entity && <SkillPage entity={entity} inDialog={true} />}
        </Overlay>
    );
}
